// Browser-side MP3 recorder that mixes the Agora local mic + remote audio tracks
// into one stereo MP3 stream using lamejs. Returns a Blob on stop().

import lamejs from 'lamejs';

const SAMPLE_RATE = 44100;
const BITRATE_KBPS = 96; // good quality for voice, small file size
const BUFFER_SIZE = 4096;

const floatTo16BitPCM = (input) => {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
};

export class CallRecorder {
  constructor() {
    this.audioContext = null;
    this.destination = null;
    this.processor = null;
    this.encoder = null;
    this.mp3Buffers = [];
    this.sources = new Map(); // key -> MediaStreamAudioSourceNode
    this.startedAt = 0;
    this.stopped = false;
  }

  start() {
    if (this.audioContext) return;
    // Use a fixed sample rate for predictable MP3 encoding.
    const Ctx = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new Ctx({ sampleRate: SAMPLE_RATE });
    this.destination = this.audioContext.createMediaStreamDestination();

    // ScriptProcessor reads the mixed destination stream and feeds PCM to lamejs.
    this.processor = this.audioContext.createScriptProcessor(BUFFER_SIZE, 2, 2);
    const destSource = this.audioContext.createMediaStreamSource(this.destination.stream);
    destSource.connect(this.processor);
    // Must be connected somewhere for onaudioprocess to fire; we never write to outputBuffer,
    // so it produces silence (no double playback of remote audio).
    this.processor.connect(this.audioContext.destination);

    this.encoder = new lamejs.Mp3Encoder(2, SAMPLE_RATE, BITRATE_KBPS);
    this.mp3Buffers = [];
    this.startedAt = Date.now();

    this.processor.onaudioprocess = (e) => {
      if (this.stopped) return;
      const left = e.inputBuffer.getChannelData(0);
      const right = e.inputBuffer.numberOfChannels > 1 ? e.inputBuffer.getChannelData(1) : left;
      const leftI16 = floatTo16BitPCM(left);
      const rightI16 = floatTo16BitPCM(right);
      const buf = this.encoder.encodeBuffer(leftI16, rightI16);
      if (buf.length > 0) this.mp3Buffers.push(buf);
    };
  }

  // Add an Agora track (local or remote) into the mix.
  // `key` lets us avoid double-adding the same track.
  addTrack(key, mediaStreamTrack) {
    if (!this.audioContext || !mediaStreamTrack) return;
    if (this.sources.has(key)) return;
    try {
      const stream = new MediaStream([mediaStreamTrack]);
      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.destination);
      this.sources.set(key, source);
    } catch (err) {
      console.warn('CallRecorder.addTrack failed:', err);
    }
  }

  removeTrack(key) {
    const src = this.sources.get(key);
    if (src) {
      try { src.disconnect(); } catch (_) { /* already disconnected */ }
      this.sources.delete(key);
    }
  }

  // Stops recording, returns { blob, durationSeconds }.
  async stop() {
    if (!this.audioContext || this.stopped) return null;
    this.stopped = true;
    try {
      const finalBuf = this.encoder.flush();
      if (finalBuf.length > 0) this.mp3Buffers.push(finalBuf);
    } catch (err) {
      console.warn('lamejs flush error:', err);
    }
    try { this.processor.disconnect(); } catch (_) { /* already disconnected */ }
    for (const src of this.sources.values()) {
      try { src.disconnect(); } catch (_) { /* already disconnected */ }
    }
    this.sources.clear();
    try { await this.audioContext.close(); } catch (_) { /* context already closed */ }

    const durationSeconds = Math.max(1, Math.round((Date.now() - this.startedAt) / 1000));
    const blob = new Blob(this.mp3Buffers, { type: 'audio/mpeg' });
    this.mp3Buffers = [];
    return { blob, durationSeconds };
  }
}
